import { pickBy, omit } from 'lodash';
import * as userAPI from '../../lib/userApi';
import patch from './patch';
import { computeUserFootprint } from './computeResults';
import Logger from '../../Logger';
import tokenNoLongerValid from './tokenNoLongerValid';
import { toast } from './toast';
import { handleUnexpectedError } from './handleUnexpectedError';
import { getLocaleFromWindowLocation } from '../../lib/routerHelper';
import { clearProfilePhotoInput, saveProfilePhoto } from './profilePhoto';
import { serializeCalculatorAnswersToState } from '../transformations/calculatorAnswers.ts';

const clear = (_dispatch, _getState) => {
  localStorage.clear();
  window.location = `/${getLocaleFromWindowLocation()}/`;
};

export const login = () => async (dispatch, getState) => {
  dispatch(patch({ ui: { spinner: true } }));
  const { user, auth } = getState();
  try {
    const { body } = await userAPI.login({ email: user.email, password: auth.password });

    dispatch(patch({
      user: body.user,
      accountForm: body.user,
      auth: { token: body.token },
      errors: { loginForm: undefined },
    }));
  } catch (err) {
    if (err.response) {
      dispatch(patch({
        errors: {
          loginForm: err.response.body,
        },
        ui: { spinner: false },
      }));
    } else {
      handleUnexpectedError(err)(dispatch, getState);
    }
    return;
  }

  dispatch(patch({ ui: { loadingCalculatorAnswers: true } }));
  const { body: { calculatorAnswers } } = await userAPI.me(getState().auth.token, { include: 'calculatorAnswers' });
  if (calculatorAnswers) {
    dispatch(patch(serializeCalculatorAnswersToState(calculatorAnswers)));
    await computeUserFootprint(calculatorAnswers.user_footprint.inputs, false)(dispatch, getState);
    dispatch(patch({ ui: { loadingCalculatorAnswers: false, spinner: false } }));
  } else {
    dispatch(patch({ ui: { loadingCalculatorAnswers: false, spinner: false } }));
  }
};

export const signup = () => async (dispatch, getState) => {
  dispatch(patch({ ui: { spinner: true } }));
  const { user, auth, ui } = getState();
  const userObj = { ...user };
  delete userObj.createdAt;
  try {
    const { body } = await userAPI.signup({
      user: userObj,
      password: auth.password,
      agreedToTermsAndConditions: ui.agreedToTermsAndConditions,
    });
    dispatch(patch({
      user: body.user,
      accountForm: body.user,
      auth: { token: body.token, password: '', passwordConfirm: '' },
      errors: { signupForm: undefined },
      ui: { spinner: false },
    }));
  } catch (err) {
    if (err.response) {
      dispatch(patch({
        errors: {
          signupForm: err.response.body,
        },
        ui: { spinner: false },
      }));
    } else {
      handleUnexpectedError(err)(dispatch, getState);
    }
  }
};

export const logout = () => async (dispatch, getState) => {
  dispatch(patch({ ui: { spinner: true } }));
  // logout from server in background. Any server failures will be logged
  // to Sentry.
  const { auth } = getState();
  try {
    await userAPI.logout(auth.token);
  } catch (err) {
    Logger.error(err);
  }
  clear(dispatch, getState);
};

export const updateUser = t => async (dispatch, getState) => {
  dispatch(patch({ ui: { spinner: true } }));
  const { accountForm, auth } = getState();
  try {
    const exclude = [
      'createdAt', 'emailVerified', 'state', 'city', 'country', 'zipcode', 'profilePhotoPreview',
      'acceptedCompetitionEntityMemberships',
    ];
    const userPayload = pickBy(
      omit(accountForm, exclude),
    );

    if (accountForm.profilePhotoPreview) {
      await saveProfilePhoto(document.getElementById('profile_photo_input').files[0], t)(dispatch, getState);
      const { accountForm: { profilePhoto } } = getState();
      userPayload.profilePhoto = profilePhoto;
    }

    const payload = { user: userPayload, password: auth.password };
    await userAPI.updateUser(payload, auth.token);
    const state = getState();
    const modal = state.ui.modal === 'saveUser' ? '' : state.ui.modal;
    dispatch(patch({
      ui: { modal, spinner: false },
      auth: { password: '' },
      user: accountForm,
      errors: { accountForm: undefined },
    }));
    toast({
      message: t('Account successfully updated'),
      style: 'success',
    })(dispatch, getState);
  } catch (err) {
    if (!err.response) {
      handleUnexpectedError(err)(dispatch, getState);
      return;
    } else if (err.response.status === 401) {
      tokenNoLongerValid();
      return;
    }
    const state = getState();

    let modal = state.ui.modal === 'saveUser' ? '' : state.ui.modal;
    const passwordIncorrect = err.response.body
      .some(validationErr => validationErr.code === userAPI.errPasswordIncorrect);
    if (passwordIncorrect) {
      modal = 'saveUser';
    }

    dispatch(patch({
      ui: { modal, spinner: false },
      errors: {
        accountForm: err.response.body,
      },
    }));
    toast({
      message: t('There are errors on the page.'),
      style: 'danger',
    })(dispatch, getState);
  }
};

export const deleteUser = () => async (dispatch, getState) => {
  dispatch(patch({ ui: { spinner: true } }));
  const { user, auth } = getState();
  try {
    await userAPI.deleteUser(user, auth.token);
    clear(dispatch, getState);
  } catch (err) {
    if (err.response && err.response.status === 401) {
      tokenNoLongerValid();
      return;
    }
    handleUnexpectedError(err)(dispatch, getState);
  }
};

export const resetAccountForm = () => (dispatch, getState) => {
  const { user } = getState();
  clearProfilePhotoInput();
  dispatch(patch({
    accountForm: {
      ...user,
      profilePhotoPreview: '',
    },
  }));
};
